import type { Scene } from '../../../core/models/scene'
import type { PersistedGraph } from '../../compass/models/KnowledgeGraph'
import type { NetworkReviewBaselineResult } from '../models/ReviewBaseline'
import { fingerprintGraphResources } from './NetworkReviewBaselineStateService'

type BaselineDeltaKind = 'added' | 'changed'
type BaselineFocusLike = { kind: string; presetId?: string }

export function isNetworkReviewBaselineDriftFocus(focus: BaselineFocusLike): boolean {
  return focus.kind === 'preset' && focus.presetId === 'drift-from-baseline'
}

export function withNetworkReviewBaselineDeltas(
  graph: PersistedGraph,
  baselineResult: NetworkReviewBaselineResult | null | undefined,
  enabled = true,
): PersistedGraph {
  if (!enabled) return graph
  const nodeDeltas = baselineDeltaNodeIds(graph, baselineResult)
  if (nodeDeltas.size === 0) return graph

  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const delta = nodeDeltas.get(node.id)
      if (!delta) return node
      return {
        ...node,
        props: {
          ...(node.props ?? {}),
          baselineDelta: delta,
          baselineId: baselineResult?.summary.baselineId ?? null,
        },
      }
    }),
  }
}

export function applyNetworkReviewBaselineSceneDeltas(
  scene: Scene,
  graph: PersistedGraph,
  baselineResult: NetworkReviewBaselineResult | null | undefined,
  enabled = true,
): Scene {
  if (!enabled) return scene
  const nodeDeltas = baselineDeltaNodeIds(graph, baselineResult)
  if (nodeDeltas.size === 0) return scene

  return {
    ...scene,
    nodes: scene.nodes.map((node) => {
      const delta = nodeDeltas.get(node.id)
      if (!delta) return node
      return {
        ...node,
        attrs: {
          ...(node.attrs ?? {}),
          baselineDelta: delta,
          baselineId: baselineResult?.summary.baselineId ?? null,
          scenarioStatus: delta,
        },
      }
    }),
  }
}

function baselineDeltaNodeIds(
  graph: PersistedGraph,
  baselineResult: NetworkReviewBaselineResult | null | undefined,
): Map<string, BaselineDeltaKind> {
  const summary = baselineResult?.summary
  if (!summary?.hasBaseline) return new Map()

  const resources = fingerprintGraphResources(graph)
  const deltas = new Map<string, BaselineDeltaKind>()
  for (const key of summary.addedResourceKeys) {
    const nodeId = resources[key]?.nodeId
    if (nodeId) deltas.set(nodeId, 'added')
  }
  for (const key of summary.changedResourceKeys) {
    const nodeId = resources[key]?.nodeId
    if (nodeId && !deltas.has(nodeId)) deltas.set(nodeId, 'changed')
  }
  return deltas
}
